import {
  AttachmentMediaType,
  MAX_ATTACHMENTS_PER_MESSAGE,
  MAX_ATTACHMENT_BYTES,
} from "@rivets/core";

/** Minimal structural view of a `File` — keeps the validation pure and
 * testable outside a DOM. */
export interface AttachmentCandidate {
  name: string;
  type: string;
  size: number;
}

export interface AttachmentIntake<T extends AttachmentCandidate> {
  accepted: { file: T; mediaType: AttachmentMediaType }[];
  /** One user-facing sentence covering everything that was turned away, or
   * `undefined` when every candidate was accepted. */
  rejection?: string;
}

export function isAttachmentMediaType(value: string): value is AttachmentMediaType {
  return AttachmentMediaType.safeParse(normalizeMediaType(value)).success;
}

/** `image/jpeg; charset=binary` and `IMAGE/PNG` both name a supported type. */
export function normalizeMediaType(value: string): string {
  return (value.split(";")[0] ?? "").trim().toLowerCase();
}

export function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

/** Splits a batch of dropped/pasted/picked files into the ones we will upload
 * and a single sentence explaining the rest. Enforces the three shared limits:
 * media type, per-file size and per-message count. */
export function intakeAttachments<T extends AttachmentCandidate>(
  files: readonly T[],
  existingCount: number,
): AttachmentIntake<T> {
  const accepted: { file: T; mediaType: AttachmentMediaType }[] = [];
  const reasons: string[] = [];
  let overflow = 0;

  for (const file of files) {
    const mediaType = attachmentMediaType(file);
    if (!isAttachmentMediaType(mediaType)) {
      reasons.push(`${describe(file)} has an invalid file type`);
      continue;
    }
    if (file.size <= 0) {
      reasons.push(`${describe(file)} is empty`);
      continue;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      reasons.push(`${describe(file)} is ${formatBytes(file.size)} (max ${formatBytes(MAX_ATTACHMENT_BYTES)})`);
      continue;
    }
    if (existingCount + accepted.length >= MAX_ATTACHMENTS_PER_MESSAGE) {
      overflow += 1;
      continue;
    }
    accepted.push({ file, mediaType: mediaType as AttachmentMediaType });
  }

  if (overflow > 0) {
    reasons.push(`only ${MAX_ATTACHMENTS_PER_MESSAGE} files can be attached to one message`);
  }
  return { accepted, rejection: reasons.length > 0 ? `${sentenceCase(reasons.join("; "))}.` : undefined };
}

/** Image files carried by a drag or paste event. `DataTransfer.files` is empty
 * for some paste sources, so items are consulted as a fallback. */
export function filesFromTransfer(transfer: DataTransfer | null): File[] {
  if (!transfer) return [];
  const fromFiles = Array.from(transfer.files);
  if (fromFiles.length > 0) return fromFiles;
  const fromItems: File[] = [];
  for (const item of Array.from(transfer.items ?? [])) {
    if (item.kind !== "file") continue;
    const file = item.getAsFile();
    if (file) fromItems.push(file);
  }
  return fromItems;
}

/** True when a drag carries files (rather than text or an internal drag). */
export function transferHasFiles(transfer: DataTransfer | null): boolean {
  if (!transfer) return false;
  return Array.from(transfer.types ?? []).includes("Files");
}

function describe(file: AttachmentCandidate): string {
  return file.name ? `“${file.name}”` : "That file";
}

export function attachmentMediaType(file: AttachmentCandidate): string {
  const declared = normalizeMediaType(file.type);
  if (declared) return declared;
  const extension = file.name.split(".").pop()?.toLowerCase();
  return ({
    pdf: "application/pdf", csv: "text/csv", md: "text/markdown", txt: "text/plain",
    json: "application/json", yaml: "application/yaml", yml: "application/yaml",
  } as Record<string, string>)[extension ?? ""] ?? "application/octet-stream";
}

function sentenceCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
